import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function RolePlay() {
  const [activeDemo, setActiveDemo] = useState<'objection' | 'discovery' | 'closing'>('objection');

  const demoScenarios = {
    objection: {
      title: 'Handling Price Objections',
      customer: '"Your solution is too expensive compared to your competitors."',
      response: '"I understand price is important. Let me show you the ROI calculation based on your specific needs..."',
      feedback: 'Great start! Consider asking a follow-up question to understand their budget constraints better.'
    },
    discovery: {
      title: 'Discovery Questions',
      customer: '"We\'re looking for a new CRM system for our growing team."',
      response: '"That\'s exciting! Can you tell me about your current process and what\'s driving the need for change?"',
      feedback: 'Excellent open-ended question! This will help you understand their pain points and requirements.'
    },
    closing: {
      title: 'Closing the Deal',
      customer: '"This looks good, but I need to think about it and discuss with my team."',
      response: '"I completely understand. What specific aspects would you like to discuss with your team?"',
      feedback: 'Good approach! You\'re uncovering potential objections while keeping the conversation moving forward.'
    }
  };

  const benefits = [
    {
      icon: 'ri-brain-line',
      title: 'Builds Muscle Memory',
      description: 'Repetitive practice creates automatic responses to common sales situations, reducing hesitation and increasing confidence.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Risk-Free Learning',
      description: 'Make mistakes and experiment with different approaches without the fear of losing real prospects or customers.'
    },
    {
      icon: 'ri-feedback-line',
      title: 'Immediate Feedback',
      description: 'Get instant, specific feedback on your performance, helping you improve faster than traditional training methods.'
    },
    {
      icon: 'ri-target-line',
      title: 'Scenario-Specific Training',
      description: 'Practice the exact situations you encounter in your role, with your products, and your customer types.'
    }
  ];

  const features = [
    {
      title: 'Adaptive AI Responses',
      description: 'Our AI adapts to your responses, creating realistic conversations that challenge you appropriately.',
      icon: 'ri-robot-line'
    },
    {
      title: 'Performance Analytics',
      description: 'Track your improvement over time with detailed analytics on response quality, timing, and effectiveness.',
      icon: 'ri-bar-chart-line'
    },
    {
      title: 'Custom Scenarios',
      description: 'Practice with scenarios built specifically for your industry, products, and customer base.',
      icon: 'ri-settings-3-line'
    },
    {
      title: 'Team Competitions',
      description: 'Engage your team with friendly competitions and leaderboards to make training fun and motivating.',
      icon: 'ri-trophy-line'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative py-20 lg:py-32 bg-gradient-to-br from-slate-900 to-teal-900 text-white overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.8), rgba(15, 23, 42, 0.8)), url('https://public.readdy.ai/ai/img_res/d757f633587d430d103fb08b55346090.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-6">
              Why Role-play is the <span className="text-teal-400">Key to Sales Mastery</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl">
              Training without practice equals wasted money. AI role-play enables safe practice, instant feedback, and real improvement that translates to revenue.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => document.querySelector('#demo-section')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-teal-700 transition-colors text-center whitespace-nowrap cursor-pointer"
              >
                See Role-play in Action
              </button>
              <Link
                to="/contact"
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-slate-900 transition-colors text-center whitespace-nowrap cursor-pointer"
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem */}
      <section className="py-20 bg-red-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Training Without Practice = Wasted Money
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Most sales training fails because it stops at information transfer. Real skills are built through practice, repetition, and feedback.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-sm text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-book-line text-3xl text-red-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Information Only</h3>
              <p className="text-gray-600">
                Traditional training provides knowledge but no opportunity to practice applying it in real situations.
              </p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-sm text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-time-line text-3xl text-red-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Delayed Application</h3>
              <p className="text-gray-600">
                Weeks or months pass between training and real customer interactions, causing knowledge to fade.
              </p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-sm text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-error-warning-line text-3xl text-red-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Learning on Prospects</h3>
              <p className="text-gray-600">
                Sales reps practice on real prospects, risking lost deals and damaged relationships during the learning process.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Demo */}
      <section id="demo-section" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Experience AI Role-play
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See how our AI creates realistic sales scenarios and provides instant feedback to improve your skills.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            {/* Scenario Selector */}
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              {Object.entries(demoScenarios).map(([key, scenario]) => (
                <button
                  key={key}
                  onClick={() => setActiveDemo(key as any)}
                  className={`px-6 py-3 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    activeDemo === key
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {scenario.title}
                </button>
              ))}
            </div>

            {/* Demo Interface */}
            <div className="bg-gray-50 rounded-xl p-8">
              <div className="space-y-6">
                <div className="bg-blue-50 p-6 rounded-lg">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-user-line text-white"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Customer Says:</h4>
                      <p className="text-gray-700 italic">{demoScenarios[activeDemo].customer}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-teal-50 p-6 rounded-lg">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-user-voice-line text-white"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Your Response:</h4>
                      <p className="text-gray-700">{demoScenarios[activeDemo].response}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 p-6 rounded-lg">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-feedback-line text-white"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">AI Feedback:</h4>
                      <p className="text-gray-700">{demoScenarios[activeDemo].feedback}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why AI Role-play Works
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our AI-powered role-play system addresses the fundamental challenges of traditional sales training.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                  <i className={`${benefit.icon} text-2xl text-teal-600`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Advanced Role-play Features
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our platform goes beyond basic role-play to provide a comprehensive training experience.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="flex items-start space-x-6 p-6 bg-gray-50 rounded-xl">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className={`${feature.icon} text-2xl text-teal-600`}></i>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-20 bg-teal-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Real Results from Role-play Training
            </h2>
            <p className="text-xl text-teal-100 max-w-3xl mx-auto">
              Companies using our AI role-play system see measurable improvements in sales performance.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">47%</div>
              <div className="text-teal-100">Increase in close rates after 30 days of practice</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">65%</div>
              <div className="text-teal-100">Reduction in objection handling time</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">89%</div>
              <div className="text-teal-100">Of reps report increased confidence</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Transform Your Sales Training?
          </h2>
          <p className="text-xl mb-8 text-gray-300">
            Stop leaving sales performance to chance. Give your team the practice they need to excel in every customer interaction.
          </p>
          <Link
            to="/contact"
            className="bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-teal-700 transition-colors inline-block whitespace-nowrap cursor-pointer"
          >
            Start Role-play Training
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
